import React, { FC, useState, useCallback } from 'react';
import { useInvokeSWRService } from '@dxb-web-ui-framework/common/hooks/DataService';
import { stringToDate } from '@dxb-web-ui-framework/common/utils';
import { subMinutes, subYears } from 'date-fns';
import { UserAppTransaction } from '@dxb-web-ui-framework/iam/domain/AccessControl/entities';
import { authorizationService } from '../../../../../channels/secure-web/services';
import {
  useSelectedCustomerId,
  useUserInfoIsUserAllowedAction,
} from '../../../../userProfile/src/contexts/UserInfo/hooks';
import { PendingAuthorizations } from '../../domain/Authorization/entities';
import NotificationsContext from './NotificationsContext';

const transactionListRequestObject = {
  fromOrderAmountFilter: 0,
  toOrderAmountFilter: 9999999999999.99,
  fromTrxDate: stringToDate({ date: subYears(new Date(), 1).toISOString(), formatExp: "yyyy-MM-dd'T'00:00:00" }),
  toTrxDate: stringToDate({
    date: subMinutes(new Date(), 1).toISOString(),
    formatExp: "yyyy-MM-dd'T'HH:mm:00",
    tz: 'UTC',
  }),
  timestamp: null,
};

const NotificationsProvider: FC = ({ children }) => {
  const [timestamp] = useState(new Date().toISOString());
  const [isNotificationsSnackbarOpen, setIsNotificationsSnackbarOpen] = useState<boolean>(true);

  const authorizationsListIsAllowed = useUserInfoIsUserAllowedAction(UserAppTransaction.PendingAuthorizationsList);

  const selectedCustomerId = useSelectedCustomerId();

  // Initiate API requests
  const shouldFetchAuthorizationsList = Boolean(selectedCustomerId) && authorizationsListIsAllowed;

  const {
    data: pendingAuthorizationsList,
    error: pendingAuthorizationsListError,
    isValidating: isLoadingPendingAuthorizations = false,
    mutate: refetchPendingAuthorizationsDataMutation,
  } = useInvokeSWRService<PendingAuthorizations>(
    authorizationService.GetPendingAuthorizations,
    {
      ...transactionListRequestObject,
      customerId: selectedCustomerId,
      meta: {
        timestamp,
      },
    },
    shouldFetchAuthorizationsList,
  );

  const onRefetchPendingAuthizationsListMutation = useCallback(() => {
    refetchPendingAuthorizationsDataMutation && refetchPendingAuthorizationsDataMutation();
  }, [refetchPendingAuthorizationsDataMutation]);

  const onToggleNotificationsSnackbar = useCallback(() => {
    setIsNotificationsSnackbarOpen(prev => !prev);
  }, []);

  const values = {
    authorizationsList: pendingAuthorizationsList?.mandateResponseList ?? [],
    transactionsList: pendingAuthorizationsList?.globalOrders ?? [],
    payrollList: pendingAuthorizationsList?.payrollMandateResponseList ?? [],
    onToggleNotificationsSnackbar,
    isNotificationsSnackbarOpen,
    loadingState: {
      isLoadingPendingAuthorizations,
    },
    errorState: {
      pendingAuthorizationsListError,
    },
    onRefetchPendingAuthizationsListMutation,
  };

  return <NotificationsContext.Provider value={values}>{children}</NotificationsContext.Provider>;
};

export { NotificationsProvider };
